var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

/**
 * Defines a "class" that sends logged DDUX events directly to the DDUX server.
 * The class offers the following methods required for any "Sender":
 *
 *    start
 *    stop
 *    isRunning
 *    sendEvent
 *
 * @copyright 2020-2021 The MathWorks, Inc.
 */
define(['dojo/io-query', 'mw-log/Log'], function (ioQuery, Log) {

    var apiKeys = {
        CLOUD_CENTER: 'fdefc29f-b188-499a-890d-30a4f7e20292',
        DESKTOP_MATLAB: 'd8c22ba3-fc0f-4beb-99fc-a109fe9abba9',
        DESKTOP_MSH: '6a221899-611e-4316-ac1c-fbe5ea3a73e6',
        DOC_FEEDBACK: '5bbcb96c-9bb4-4aa2-8ac5-e9f6901ce151',
        MATLAB_HOME: '0e53a0f2-a207-4268-90c0-0af01305332c',
        MATLAB_MOBILE: 'c576cb32-359d-4404-856e-cc5d1be06ce0',
        MATLAB_ONLINE: '86d464a0-b0c4-4c8d-a2bd-fa21d43f91f9',
        ONLINE_COURSES: '96d79d60-e6f9-48da-8fc6-7358f335406a',
        POLYSPACE: 'af6fa465-294f-4b72-a942-b53571cbd423',
        PRODUCT_INSTALLER_V2: '86aa5b7b-e4b6-4b3f-9226-7d816bea98aa',
        UNIFIED_ADDON_INSTALLER: 'b09519c4-8e49-4dee-b1eb-a03e4b11627a',
        UPDATE_INSTALLER_V2: 'af238f5a-6efa-4f62-a77e-66b9c72f4b1f',
        WEB_PAGE_METRICS: '2369b91c-a64f-11ec-b909-0242ac120002'
    };
    var _started;
    var _httpProperties;
    var _sessionKey;
    var _postURL = '';
    var cleanUpEventType = function cleanUpEventType(eventType) {
        if (!eventType || eventType === 'click') {
            return 'clicked';
        }
        return eventType.replaceAll('_', ' ');
    };
    var cleanUpElementType = function cleanUpElementType(elementType) {
        if (!elementType) {
            return 'none';
        }
        return elementType.replaceAll('_', ' ');
    };

    var getIntegNumberForRelease = function getIntegNumberForRelease(release) {
        var indexToIntegMap = [1, 3, 2];
        if (release && release.length === 6) {
            var year = parseInt(release.substring(1, 5));
            var aorb = release.charAt(5);
            var index = ((year - 2016) * 2 + (aorb === 'a' ? 0 : 1)) % 3;
            return indexToIntegMap[index];
        }
    };

    var generateSessionKey = function generateSessionKey() {
        // This code is copied from mw-utils/Utils.generateUuid which only generates 8 characters.
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            /*jslint bitwise: false */
            var r = Math.random() * 16 | 0,
                v = c === 'x' ? r : r & 0x3 | 0x8;
            /*jslint bitwise: true */
            return v.toString(16);
        });
    };

    var getPostUrlFromPropertiesAndHostname = function getPostUrlFromPropertiesAndHostname(properties, hostname) {
        var postURL = 'https://udc-service';
        var sendToDev = properties.sendToDev;
        var integNumber = void 0;
        if (sendToDev !== false) {
            // If hostname contains "integ" send to dev and also extract the integ number (if present)
            var integIndex = hostname.indexOf('integ');
            if (integIndex >= 0) {
                sendToDev = true;
                integNumber = hostname.substring(integIndex + 5, integIndex + 6);
                if (!(integNumber === '1' || integNumber === '2' || integNumber === '3')) {
                    integNumber = undefined;
                }
            } else {
                // Send to prod if hostname contains "mathworks." (but not "integ")
                // Note: "mathworks." covers both "mathworks.com" and "mathworks.cn"
                sendToDev = !hostname.includes('mathworks.');
            }
            if (sendToDev) {
                integNumber = properties.endPointNumber || integNumber || getIntegNumberForRelease(properties.release) || 1;
                postURL += '-integ' + integNumber;
            }
        }
        postURL += '.mathworks.com/udc/service/v1/events';
        return postURL;
    };

    return {
        /**
         * Initializes by storing relevant properties
         *
         * @param properties {object} supplies information needed to communicate with the server
         *        and optional event bundling control parameters
         * @param properties.applicationName the name of the application logging data (required)
         * @param properties.applicationVersion the version of the application loggig data (required)
         * @param properties.sendToDev specifies whether logged data should be send to the devevelopment (dev)
         *        or production (prod) URL. If not specified data goes to prod if the page hostname contains "mathworks."
         *        (optional)
         *
         * @instance
         */
        start: function start(properties) {
            if (_started) {
                return;
            }
            _started = true;
            Log.assert(properties.applicationName, 'applicationName must be supplied for direct send');
            Log.assert(properties.applicationVersion, 'applicationVersion must be supplied for direct send');
            _httpProperties = {
                applicationName: properties.applicationName,
                applicationVersion: properties.applicationVersion,
                applicationKey: properties.applicationKey
            };
            _sessionKey = generateSessionKey();

            // See if the page URL specifies a DDUX end point override
            var url = location.href;
            var queryIndex = url.indexOf('?');
            if (queryIndex >= 0) {
                var queryParameters = ioQuery.queryToObject(url.substring(queryIndex + 1, url.length));
                if (queryParameters.dduxUrlOverride) {
                    _postURL = queryParameters.dduxUrlOverride;
                }
            }
            if (!_postURL) {
                _postURL = getPostUrlFromPropertiesAndHostname(properties, window.location.hostname);
            }
        },

        /**
         * Terminates communication with the backend MATLAB
         *
         * @instance
         */
        stop: function stop() {
            if (_started) {
                _started = false;
            }
        },

        /**
         * Indicates whether the sender has been started and not stopped
         *
         * @returns {boolean} true if start, but not stop has been called.
         *
         * @instance
         */
        isRunning: function isRunning() {
            return _started;
        },

        /**
         * Sends an event to the server, either immediately or when bundle send is triggered
         *
         * @param eventType "telemetry", "uiEvent" or "composition"
         * @param event the attributes that define the event
         */
        sendEvent: function sendEvent(eventType, event) {
            var eventData;
            var eventKey;
            if (eventType === 'telemetry') {
                /* Example telemetry event data
                  "eventData": {
                    "logDDUXData": {
                      "product": "ML",
                      "keyValues": {
                        "activityState": "active",
                        "activityType": "UI",
                        "previousState": "idle",
                        "transitionTime": "2020-12-24T13:32:41.876"
                      }
                    }
                  }
                */
                eventData = {
                    logDDUXData: {
                        product: event.product,
                        keyValues: event.data
                    }
                };
                eventKey = event.eventKey;
            } else if (eventType === 'uiEvent') {
                /* Example UI event data
                   "eventData": {
                     "uiEvent": {
                       "productName": "MATLAB",
                       "uiScope": "App Designer",
                       "elementId": "appDesignerToolstrip.layoutTab.tab",
                       "elementType": "toolstrip tab",
                       "eventType": "clicked",
                       "values": {
                         "entry": [
                           {"key": "subscope",
                            "value": "toolstrip"
                           }
                         ]
                       }
                     }
                */
                eventData = {
                    uiEvent: {
                        productName: event.product,
                        uiScope: event.scope,
                        elementId: event.elementId,
                        elementType: cleanUpElementType(event.elementType),
                        eventType: cleanUpEventType(event.eventType)
                    }
                };
                eventKey = 'MATLAB_DDUX_UI_EVENT';
                // Find and store any key-value pairs other than the above standard pairs
                var keyValuePairs = [];
                var _iteratorNormalCompletion = true;
                var _didIteratorError = false;
                var _iteratorError = undefined;

                try {
                    for (var _iterator = Object.entries(event)[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                        var _step$value = _slicedToArray(_step.value, 2);

                        key = _step$value[0];
                        value = _step$value[1];

                        if (key !== 'product' && key !== 'scope' && key !== 'elementId' && key !== 'elementType' && key !== 'eventType') {
                            if (key === 'subScope') {
                                key = 'subscope';
                            }
                            keyValuePairs.push({ 'key': key, 'value': value });
                        }
                    }
                } catch (err) {
                    _didIteratorError = true;
                    _iteratorError = err;
                } finally {
                    try {
                        if (!_iteratorNormalCompletion && _iterator.return) {
                            _iterator.return();
                        }
                    } finally {
                        if (_didIteratorError) {
                            throw _iteratorError;
                        }
                    }
                }

                if (keyValuePairs.length > 0) {
                    eventData['values'] = {
                        entry: keyValuePairs
                    };
                }
            } else {
                Log.warn('Direct send to server not yet supported for ' + eventType + ' events');
                return;
            }
            var eventDataString = JSON.stringify(eventData);

            var request = new XMLHttpRequest();
            request.open('POST', _postURL);
            request.setRequestHeader('Content-type', 'application/json');
            request.setRequestHeader('x-mw-udc-client-version', '1.0');
            request.setRequestHeader('x-mw-udc-application-name', _httpProperties.applicationName);
            request.setRequestHeader('x-mw-udc-application-version', _httpProperties.applicationVersion);
            request.setRequestHeader('x-mw-authentication', _httpProperties.applicationKey || apiKeys[_httpProperties.applicationName]);
            var eventEntry = {
                sessionKey: _sessionKey,
                eventKey: eventKey,
                eventDate: new Date().toISOString().slice(0, 23), // Slice off trailing 'Z'
                eventData: eventDataString
            };
            if (event.appComponent) {
                eventEntry.applicationComponent = event.appComponent;
            }
            var message = {
                Event: [eventEntry]
            };
            request.addEventListener('error', function () {
                Log.warn('DDUX http request failed');
            });
            /*
            request.addEventListener('load', function() {
                Log.info('DDUX http request succeeded');
            });
            */
            request.send(JSON.stringify(message));
        }
    };
});
//# sourceMappingURL=DirectSender.js.map
